import { CSSResultGroup, TemplateResult, html, svg } from 'lit';
import { OutlineElement } from '@phase2/outline-core';
import {
  customElement,
  property,
  queryAssignedElements,
} from 'lit/decorators.js';
import componentStyles from './outline-video-player.css.lit';

export type VideoVariations = 'player' | 'hero' | 'modal' | 'inline';

/**
 * The OutlineVideoPlayer component
 * @element outline-video-player
 */
@customElement('outline-video-player')
export class OutlineVideoPlayer extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  /**
   * Set when slotted content is verified
   */
  @property({ type: Boolean })
  hasVideo = false;

  /**
   * Sets the version of the player.
   * hero: Removes video controls attribute
   * and places play/pause button in bottom right corner. Forces video to cover.
   *
   * player: Leaves video controls, places play/pause in center of the screen,
   * leaves video at natural 16/9 aspect ratio.
   */
  @property({ type: String })
  variant: VideoVariations = 'hero';

  /**
   * Sets the poster image for the video.
   */
  @property({ type: String })
  poster: string;

  /**
   * Query for default slot
   */
  @queryAssignedElements({})
  slottedContent: HTMLElement[];

  /**
   * Makes sure video autoplays on load.
   */
  @property({ type: Boolean })
  playing = false;

  /**
   * Whether to render a play/pause button on top of the video.
   */
  @property({
    type: Boolean,
    attribute: 'display-toggle-button',
    reflect: true,
  })
  hideToggleButton = false;

  /**
   * ref to slotted video element.
   */
  videoContent: HTMLMediaElement;

  firstUpdated() {
    this.getVideo();
  }

  render(): TemplateResult {
    return html`
      <div class="outline-video ${this.variant}">
        <slot></slot>
        ${this.hasVideo ? this.buttonTemplate() : null}
      </div>
    `;
  }

  /**
   * Overrides video:-webkit-full-page-media styles
   * to make sure slotted video is always full width
   * @param video
   */
  handleVideoAttributes(video: HTMLMediaElement) {
    if (video) {
      if (this.variant === 'hero') {
        video.setAttribute('loop', 'loop');
      }
      if (this.variant === 'player') {
        video.removeAttribute('autoplay');
      }
      video.style.width = '100%';
      video.style.objectFit = 'cover';
      video.style.height = '100%';
    }
  }

  buttonTemplate() {
    return html`
      ${!this.hideToggleButton
        ? html`
            <button
              class="outline-video--button"
              id="outline-video-button"
              aria-pressed="${this.playing}"
              aria-label="Pause video"
              @click=${this.pauseVideo}
            >
              ${this.playing ? this.pauseIcon() : this.playIcon()}
            </button>
          `
        : null}
    `;
  }

  playIcon() {
    return svg`<svg xmlns="http://www.w3.org/2000/svg" width="11" height="14" fill="none" viewBox="0 0 9 14"><path fill="#041e4a" d="M0 1.82v10.36c0 .79.87 1.27 1.54.84l8.14-5.18a1 1 0 0 0 0-1.69L1.54.98A.998.998 0 0 0 0 1.82Z"/></svg>`;
  }

  pauseIcon() {
    return svg`<svg xmlns="http://www.w3.org/2000/svg" width="12" height="14" fill="none" viewBox="0 0 12 14"><path fill="#041e4a" d="M0 14h4V0H0v14ZM8 0v14h4V0H8Z"/></svg>`;
  }

  pauseVideo() {
    if (this.playing) {
      this.videoContent.pause();
    } else {
      this.videoContent.play();
    }
    return (this.playing = !this.playing);
  }

  getVideo() {
    const slotted = this.slottedContent[0] as HTMLIFrameElement;
    const video = slotted?.contentWindow?.document.querySelector(
      'video'
    ) as HTMLMediaElement;
    const iframe = slotted?.querySelector('iframe') as HTMLIFrameElement;
    const videoEl = slotted?.querySelector('video') as HTMLMediaElement;
    if (iframe) {
      if (this.variant === 'inline') {
        setElementStyles(iframe, {
          width: '100%',
          height: '100%',
          aspectRatio: '16/9',
        });
      } else {
        setElementStyles(iframe, { width: '100%', height: '100%' });
        setElementStyles(slotted, { height: '100%' });
      }
    }

    if (videoEl) {
      setElementStyles(videoEl, { width: '100%', height: '100%' });

      if (this.poster) {
        videoEl.setAttribute('poster', this.poster);
      }
    }

    if (slotted instanceof HTMLVideoElement) {
      setElementStyles(slotted, {
        width: '100%',
        height: '100%',
        aspectRatio: '16/9',
      });
    }

    function setElementStyles(
      element: HTMLElement,
      styles: Partial<CSSStyleDeclaration>
    ) {
      Object.assign(element.style, styles);
    }
    if (video === null || video === undefined) {
      // bail
      return;
    } else {
      this.hasVideo = true;
      this.videoContent = video;
      if (this.variant === 'hero') {
        // REQUIRED for google and safari to autoplay.
        this.playing = true;
        video.muted = true;
        video.paused ? video.play() : null;
      } else {
        this.pauseVideo();
      }
      this.handleVideoAttributes(this.videoContent);
    }
  }
}
